import { Skeleton } from "@assetwatch/ui";
import { Suspense, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useEnterpriseStore } from "../state/enterpriseStore";
import type { AskReliFloatingChatProps } from "./AskReli.types";
import { AskReliFloatingChatContent } from "./AskReliFloatingChatContent";
import { AskReliFloatingErrorBoundary } from "./AskReliFloatingErrorBoundary";
import { AskReliFloatingErrorFallback } from "./AskReliFloatingFallbackShell";

const backdropClassName =
  "fixed inset-0 z-[1190] cursor-default border-0 bg-transparent p-0";
const chatRootClassName =
  "fixed bottom-(--spacing-24) right-(--spacing-24) z-[1200]";
const viewportBaseClassName =
  "transition-[opacity,transform,padding] duration-[220ms] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none max-sm:fixed max-sm:inset-x-0 max-sm:bottom-0 max-sm:flex max-sm:justify-end";
const panelBaseClassName =
  "origin-bottom-right transition-[height,width] duration-[220ms] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none";
const collapsedPanelSizeClassName =
  "h-[min(640px,calc(100vh_-_96px))] w-[min(430px,calc(100vw_-_var(--spacing-32)))] max-sm:h-[min(540px,calc(100vh_-_96px))] max-sm:w-[min(360px,calc(100vw_-_var(--spacing-24)))]";
const expandedPanelSizeClassName =
  "h-[min(680px,calc(100vh_-_96px))] w-[min(900px,calc(100vw_-_var(--spacing-32)))] max-sm:h-[min(86dvh,calc(100dvh_-_var(--spacing-24)))] max-sm:w-full";

function getViewportClassName(isOpen: boolean, isExpanded: boolean) {
  return [
    viewportBaseClassName,
    isOpen
      ? "translate-y-0 opacity-100"
      : "translate-y-(--spacing-16) opacity-0",
    isExpanded
      ? "max-sm:p-0"
      : "max-sm:px-(--spacing-12) max-sm:pb-(--spacing-12)",
  ].join(" ");
}

function getPanelClassName(isExpanded: boolean) {
  return [
    panelBaseClassName,
    isExpanded ? expandedPanelSizeClassName : collapsedPanelSizeClassName,
  ].join(" ");
}

export function AskReliFloatingChat({
  externalCustomerId,
  contextLabel,
  userName,
}: AskReliFloatingChatProps) {
  const { t } = useTranslation();
  const isOpen = useEnterpriseStore((state) => state.isAskReliOpen);
  const hasOpened = useEnterpriseStore((state) => state.hasAskReliOpened);
  const isExpanded = useEnterpriseStore((state) => state.isAskReliExpanded);
  const onClose = useEnterpriseStore((state) => state.closeAskReli);
  const onToggleExpand = useEnterpriseStore(
    (state) => state.toggleAskReliExpanded,
  );
  const chatRootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) return;
    const activeElement = document.activeElement;
    if (
      activeElement instanceof HTMLElement &&
      chatRootRef.current?.contains(activeElement)
    ) {
      activeElement.blur();
    }
  }, [isOpen]);

  if (!hasOpened) {
    return null;
  }

  return (
    <>
      {isOpen ? (
        <button
          type="button"
          className={backdropClassName}
          aria-label={t("enterprise.reliability.assistant.closeChat")}
          onClick={onClose}
        />
      ) : null}
      <div
        ref={chatRootRef}
        className={`${chatRootClassName} ${
          isOpen ? "pointer-events-auto" : "pointer-events-none"
        }`}
        inert={!isOpen || undefined}
        data-testid="ask-reli-chat"
      >
        <div className={getViewportClassName(isOpen, isExpanded)}>
          <section
            className={getPanelClassName(isExpanded)}
            role="dialog"
            aria-modal="false"
            aria-label={t("enterprise.reliability.assistant.chatDialogAria")}
          >
            <AskReliFloatingErrorBoundary
              resetKey={`${externalCustomerId ?? "no-customer"}-${isOpen}`}
              fallback={(onRetry) => (
                <AskReliFloatingErrorFallback
                  isExpanded={isExpanded}
                  onClose={onClose}
                  onRetry={onRetry}
                  onToggleExpand={onToggleExpand}
                />
              )}
            >
              <Suspense
                fallback={<Skeleton className="h-full w-full" radius="lg" />}
              >
                <AskReliFloatingChatContent
                  contextLabel={contextLabel}
                  externalCustomerId={externalCustomerId}
                  isExpanded={isExpanded}
                  userName={userName}
                  onClose={onClose}
                  onToggleExpand={onToggleExpand}
                />
              </Suspense>
            </AskReliFloatingErrorBoundary>
          </section>
        </div>
      </div>
    </>
  );
}
